import { deadlineGuardConfig } from '~/config/deadlineGuardConfig';

/**
 * T-239 — jedyna warstwa HTTP aplikacji dla REST API DocuWatcher: żądania z tokenem Firebase,
 * normalizacja wszystkich kształtów błędów backendu do `ApiError` oraz daty Jacksona.
 * Moduły `api/<domena>.ts` zostają cienkie (typy + funkcje na `requestJson`/`requestBlob`).
 */

/** Pojedynczy błąd pola z `ValidationErrorResponseDTO` (400, Bean Validation). `field` jest `null`
 * dla naruszeń na poziomie klasy (T-70). */
export interface FieldErrorDTO {
  field: string | null;
  message: string;
}

/** Wynik odczytania ciała odpowiedzi błędu — wspólny dla wszystkich kształtów DTO backendu. */
export interface ParsedErrorBody {
  message?: string;
  ruleCode?: string;
  details?: Record<string, unknown>;
  fieldErrors?: FieldErrorDTO[];
  retryable?: boolean;
  /** Surowe `data.message` (bez sklejania `errors[]`) — dla domen, które zachowują dotychczasowe komunikaty. */
  rawMessage?: string;
  /** Surowe `data.error` (np. odpowiedzi Dysku). */
  rawError?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public ruleCode?: string,
    public details?: Record<string, unknown>,
    public fieldErrors?: FieldErrorDTO[]
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** true dla HTTP 409 (`ruleCode="CONCURRENT_MODIFICATION_RETRY"`, P-24) — retry, nie błąd reguły biznesowej. */
  get isConcurrencyConflict(): boolean {
    return this.status === 409;
  }
}

/**
 * Backend serializuje `LocalDate` jako tablicę JSON `[rok, miesiąc, dzień]` (Jackson
 * `WRITE_DATES_AS_TIMESTAMPS`), nie jako string ISO. Normalizuje oba warianty do `"yyyy-MM-dd"`,
 * żeby reszta frontendu nigdy nie musiała o tym wiedzieć.
 */
export function parseLocalDate(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  if (Array.isArray(raw)) {
    const [year, month, day] = raw as number[];

    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  return String(raw);
}

/**
 * Analogicznie do {@link parseLocalDate}, ale dla `Instant`. W przeciwieństwie do `LocalDate`
 * backend serializuje go jako pojedynczą liczbę zmiennoprzecinkową `epochSekundy.ułamekNanosekund`
 * (potwierdzone rzeczywistym wywołaniem API). Obsługuje też wariant tablicowy defensywnie, na
 * wypadek przyszłej zmiany konfiguracji serializacji.
 */
export function parseInstant(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  if (Array.isArray(raw)) {
    const [epochSecond, nanoAdjustment] = raw as number[];

    return new Date(epochSecond * 1000 + Math.round((nanoAdjustment ?? 0) / 1e6)).toISOString();
  }
  if (typeof raw === 'number') {
    return new Date(raw * 1000).toISOString();
  }

  return String(raw);
}

/**
 * `MassBalanceBusinessRuleException.details` może zawierać `affectedBatches` (np.
 * `PERIOD_CLOSURE_DEFICIT`, T-37/F-08) oraz pola `LocalDate` na najwyższym poziomie (T-144,
 * T-195) — bez tej normalizacji `loadingDate` itp. docierałyby do wywołującego kodu w surowym
 * formacie tablicowym `[rok, miesiąc, dzień]`.
 */
function normalizeErrorDetails(details: Record<string, unknown>): Record<string, unknown> {
  let normalized = details;
  if (Array.isArray(details.affectedBatches)) {
    normalized = {
      ...normalized,
      affectedBatches: (details.affectedBatches as { loadingDate?: unknown }[]).map((batch) => ({
        ...batch,
        loadingDate: parseLocalDate(batch.loadingDate),
      })),
    };
  }
  for (const key of ['loadingDate', 'periodStart', 'periodEnd', 'rangeStart', 'rangeEnd']) {
    if (key in normalized) {
      normalized = { ...normalized, [key]: parseLocalDate(normalized[key]) };
    }
  }

  return normalized;
}

/**
 * Czyta ciało odpowiedzi błędu. Obsługuje kształty: `ErrorResponseDTO` (`message`, `code`),
 * `MassBalanceErrorResponseDTO` (`ruleCode`, `details`) i `ValidationErrorResponseDTO`
 * (`errors[]` — 400 z ogólnym „Validation failed" w `message`, właściwy powód w `errors[].message`,
 * T-107). Nieczytelne ciało daje pusty wynik.
 */
export async function readErrorBody(response: Response): Promise<ParsedErrorBody> {
  try {
    const data = await response.json();
    const details =
      data?.details && typeof data.details === 'object'
        ? normalizeErrorDetails(data.details as Record<string, unknown>)
        : undefined;

    const rawFieldErrors = Array.isArray(data?.errors)
      ? (data.errors as { field?: string | null; message?: string }[]).filter(
          (fieldError): fieldError is { field: string | null; message: string } =>
            typeof fieldError?.message === 'string' && fieldError.message.length > 0
        )
      : [];
    const fieldErrorMessages = rawFieldErrors.map((fieldError) => fieldError.message);

    return {
      message:
        fieldErrorMessages.length > 0
          ? fieldErrorMessages.join(' ')
          : typeof data?.message === 'string'
            ? data.message
            : typeof data?.error === 'string'
              ? data.error
              : undefined,
      // T-275 — 400 z `ErrorResponseDTO` niesie kod w `code`, nie w `ruleCode`
      ruleCode:
        typeof data?.ruleCode === 'string'
          ? data.ruleCode
          : typeof data?.code === 'string'
            ? data.code
            : undefined,
      details,
      // T-207 — obok sklejonego `message`, żeby nierozpoznany kształt walidacji dało się pokazać per pole
      fieldErrors:
        rawFieldErrors.length > 0
          ? rawFieldErrors.map((fieldError) => ({
              field: fieldError.field ?? null,
              message: fieldError.message,
            }))
          : undefined,
      retryable: typeof data?.retryable === 'boolean' ? data.retryable : undefined,
      rawMessage: typeof data?.message === 'string' ? data.message : undefined,
      rawError: typeof data?.error === 'string' ? data.error : undefined,
    };
  } catch {
    return {};
  }
}

/** Buduje błąd wyrzucany dla odpowiedzi nie-2xx; domyślnie `ApiError`, domeny podstawiają swoją klasę. */
export type ErrorFactory = (status: number, body: ParsedErrorBody) => unknown;

export const defaultErrorFactory: ErrorFactory = (status, body) =>
  new ApiError(
    body.message || `HTTP error! status: ${status}`,
    status,
    body.ruleCode,
    body.details,
    body.fieldErrors
  );

export async function throwApiError(
  response: Response,
  createError: ErrorFactory = defaultErrorFactory
): Promise<never> {
  const body = await readErrorBody(response);

  throw createError(response.status, body);
}

export type QueryValue = string | number | boolean | undefined | null;

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /** Dokleja `workspaceId` aktualnie wybranego workspace'u dla ścieżek tenantowych. */
  scoped?: boolean;
  createError?: ErrorFactory;
}

function buildUrl(path: string, options: RequestOptions): string {
  const base = options.scoped
    ? deadlineGuardConfig.getScopedEndpointUrl(path)
    : deadlineGuardConfig.getEndpointUrl(path);
  if (!options.query) return base;

  const url = new URL(base);
  for (const [key, value] of Object.entries(options.query)) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers = await deadlineGuardConfig.getAuthHeaders();
  const init: NonNullable<Parameters<typeof fetch>[1]> = {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  };
  if (options.signal) {
    init.signal = options.signal;
  }
  const response = await fetch(buildUrl(path, options), init);

  if (!response.ok) {
    await throwApiError(response, options.createError);
  }

  return response;
}

export async function requestJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  // 204 i każda odpowiedź 2xx z pustym ciałem (np. DELETE z 200 bez treści) to „brak wyniku”, nie błąd JSON.
  if (response.status === 204 || response.headers?.get?.('content-length') === '0') {
    return undefined as T;
  }
  if (typeof response.text !== 'function') {
    return response.json();
  }
  const text = await response.text();

  return (text.trim() === '' ? undefined : JSON.parse(text)) as T;
}

/** Żądanie z ciałem JSON, którego odpowiedzią jest tekst (np. podgląd dokumentu PoS w HTML). */
export async function requestText(path: string, options: RequestOptions = {}): Promise<string> {
  const response = await send(path, options);

  return response.text();
}

export async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const response = await send(path, options);

  return response.blob();
}
